import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import lookupService from '../../../services/lookupService';
import {
  filterIcdOptions,
  findIcdOptionByCode,
  formatIcdOptionLabel,
  getIcdOptionCode,
  mergeIcdSearchResults,
} from '../../../utils/patientMonitoringIcdUtils';
import './IcdCodeSearchField.css';

const SEARCH_DEBOUNCE_MS = 250;
const MENU_MAX_HEIGHT = 240;
const MENU_MIN_WIDTH = 320;

function selectedDisplayText(value, localOptions) {
  const code = String(value || '').trim();
  if (!code) return '';
  const match = findIcdOptionByCode(localOptions, code);
  return match ? formatIcdOptionLabel(match) : code;
}

function getMenuPosition(inputEl) {
  if (!inputEl) return null;
  const rect = inputEl.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = Math.min(Math.max(rect.width, MENU_MIN_WIDTH), Math.max(vw - 16, rect.width));
  let left = rect.left;
  if (left + width > vw - 8) {
    left = Math.max(8, vw - 8 - width);
  }
  if (left < 8) left = 8;

  const spaceBelow = vh - rect.bottom - 8;
  const spaceAbove = rect.top - 8;
  const maxHeight = Math.min(MENU_MAX_HEIGHT, Math.max(spaceBelow, spaceAbove, 120));
  const openUp = spaceBelow < 140 && spaceAbove > spaceBelow;
  const top = openUp
    ? Math.max(8, rect.top - 4 - maxHeight)
    : rect.bottom + 4;

  return { top, left, width, maxHeight };
}

export default function IcdCodeSearchField({
  value = '',
  onChange,
  localOptions = [],
  disabled = false,
  placeholder = 'Search ICD code',
  ariaLabel = 'ICD code',
  inputClassName = '',
}) {
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const requestIdRef = useRef(0);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [remoteOptions, setRemoteOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [menuPos, setMenuPos] = useState(null);

  const displayValue = open ? query : selectedDisplayText(value, [...localOptions, ...remoteOptions]);

  const results = useMemo(() => {
    const q = String(query || '').trim().toLowerCase();
    const localMatches = filterIcdOptions(localOptions, query);
    const merged = !q ? localMatches : mergeIcdSearchResults(remoteOptions, localMatches);
    if (!q) return merged;
    return [...merged].sort((a, b) => {
      const rank = (option) => {
        const code = getIcdOptionCode(option).toLowerCase();
        const name = String(option?.name || '').toLowerCase();
        if (code.startsWith(q)) return 0;
        if (code.includes(q)) return 1;
        if (name.startsWith(q)) return 2;
        return 3;
      };
      const diff = rank(a) - rank(b);
      if (diff !== 0) return diff;
      return getIcdOptionCode(a).localeCompare(getIcdOptionCode(b));
    });
  }, [localOptions, remoteOptions, query]);

  useEffect(() => {
    if (!open || disabled) return undefined;

    const q = String(query || '').trim();
    if (!q) {
      requestIdRef.current += 1;
      setRemoteOptions([]);
      setLoading(false);
      return undefined;
    }

    const requestId = ++requestIdRef.current;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await lookupService.searchIcdCodes(q);
        if (requestId !== requestIdRef.current) return;
        const rows = Array.isArray(res?.data) ? res.data : [];
        setRemoteOptions(rows);
      } catch {
        if (requestId !== requestIdRef.current) return;
        setRemoteOptions([]);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query, open, disabled]);

  useEffect(() => {
    setHighlightIndex(0);
  }, [results, query]);

  useEffect(() => {
    if (!open || disabled) {
      setMenuPos(null);
      return undefined;
    }
    const update = (event) => {
      const scrollTarget = event?.target;
      if (scrollTarget && dropdownRef.current?.contains(scrollTarget)) return;
      setMenuPos(getMenuPosition(inputRef.current));
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, disabled, results.length]);

  useEffect(() => {
    if (!open || disabled) return undefined;
    const el = dropdownRef.current;
    if (!el) return undefined;
    const stopNativeScrollLock = (event) => {
      event.stopPropagation();
    };
    el.addEventListener('wheel', stopNativeScrollLock);
    el.addEventListener('touchmove', stopNativeScrollLock);
    return () => {
      el.removeEventListener('wheel', stopNativeScrollLock);
      el.removeEventListener('touchmove', stopNativeScrollLock);
    };
  }, [open, disabled, menuPos]);

  useEffect(() => {
    const onPointerDown = (event) => {
      const target = event.target;
      if (wrapRef.current?.contains(target) || dropdownRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  const selectOption = (option) => {
    const code = getIcdOptionCode(option);
    onChange?.(code);
    setQuery(formatIcdOptionLabel(option));
    setOpen(false);
  };

  const handleFocus = () => {
    if (disabled) return;
    setQuery(String(value || '').trim());
    setOpen(true);
  };

  const handleChange = (event) => {
    const next = event.target.value;
    setQuery(next);
    setOpen(true);
    if (!String(next).trim()) onChange?.('');
  };

  const handleKeyDown = (event) => {
    if (disabled) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setHighlightIndex((i) => Math.min(i + 1, Math.max(results.length - 1, 0)));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (event.key === 'Enter' && open && results[highlightIndex]) {
      event.preventDefault();
      selectOption(results[highlightIndex]);
      return;
    }
    if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  const dropdown = open && !disabled && menuPos
    ? createPortal(
        <ul
          ref={dropdownRef}
          className="icd-search-dropdown"
          role="listbox"
          onWheel={(event) => event.stopPropagation()}
          onTouchMove={(event) => event.stopPropagation()}
          style={{
            top: menuPos.top,
            left: menuPos.left,
            width: menuPos.width,
            maxHeight: menuPos.maxHeight,
          }}
        >
          {loading && results.length === 0 && (
            <li className="icd-search-empty">Searching…</li>
          )}
          {!loading && results.length === 0 && (
            <li className="icd-search-empty">
              {String(query || '').trim() ? 'No matching ICD codes' : 'Type a code or diagnosis'}
            </li>
          )}
          {results.map((option, index) => {
            const code = getIcdOptionCode(option);
            return (
              <li key={code}>
                <button
                  type="button"
                  role="option"
                  aria-selected={code === value}
                  className={`icd-search-option${index === highlightIndex ? ' is-active' : ''}${code === value ? ' is-selected' : ''}`}
                  onMouseEnter={() => setHighlightIndex(index)}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => selectOption(option)}
                >
                  {formatIcdOptionLabel(option)}
                </button>
              </li>
            );
          })}
        </ul>,
        document.body
      )
    : null;

  return (
    <div className={`icd-search${open ? ' is-open' : ''}`} ref={wrapRef}>
      <input
        ref={inputRef}
        type="text"
        className={`icd-search-input ${inputClassName}`.trim()}
        value={displayValue}
        disabled={disabled}
        placeholder={placeholder}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-autocomplete="list"
        autoComplete="off"
        role="combobox"
        onFocus={handleFocus}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
      />
      {dropdown}
    </div>
  );
}
